const isGroup = process.env.NEXT_PUBLIC_IS_GROUP === 'true' || false

export const formatPrice = (price: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(price)

// prix abrégé (étiquettes de carte) : « 850 € », « 245K € », « 1,2M € »
export const formatCompactPrice = (price: number) => {
  const thousands = Math.floor(price / 1000)
  if (thousands >= 1000) return `${(price / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}M €`
  if (price >= 10_000) return `${thousands}K €`
  return formatPrice(price)
}

// « 1 maison », « 3 maisons »
export const plural = (count: number, word: string) => `${count} ${word}${count > 1 ? 's' : ''}`

// ['A', 'B', 'C'] → « A, B et C » (ou « A, B ou C » avec 'disjunction')
export const listFr = (items: string[], type: Intl.ListFormatType = 'conjunction') => new Intl.ListFormat('fr', { type }).format(items)

// valeurs distinctes triées par nombre d'occurrences décroissant : ['Lyon', 'Lyon', 'Vienne'] → ['Lyon', 'Vienne']
export const rankByCount = <T,>(items: T[], getKey: (item: T) => string | null | undefined) => Object.entries(
  items.reduce<Record<string, number>>((acc, item) => {
    const key = getKey(item)
    if (key) acc[key] = (acc[key] ?? 0) + 1
    return acc
  }, {})
).sort(([, a], [, b]) => b - a).map(([key]) => key)

// nombre de valeurs distinctes
export const countDistinct = <T,>(items: T[], getKey: (item: T) => string | null | undefined) =>
  new Set(items.map(getKey).filter(Boolean)).size

// contraction devant un nom de commune : « à Lyon », « au Puy-en-Velay », « aux Roches-de-Condrieu », « de La Talaudière »…
export const withPreposition = (preposition: 'à' | 'de', city: string) => {
  const match = /^(Le|Les) (.+)$/.exec(city)
  if (!match) return `${preposition} ${city}`
  const [, article, rest] = match
  const contracted = preposition === 'à'
    ? (article === 'Le' ? 'au' : 'aux')
    : (article === 'Le' ? 'du' : 'des')
  return `${contracted} ${rest}`
}

// préposition d'usage devant un nom de département, par code (irrégulière : « dans la Loire », « en Isère », « dans l'Ain »…)
const DEPARTMENT_PREPOSITIONS: Record<string, string> = {
  '01': "dans l'", '02': "dans l'", '03': "dans l'", '04': 'dans les', '05': 'dans les', '06': 'dans les', '07': 'en', '08': 'dans les', '09': 'en',
  '10': "dans l'", '11': "dans l'", '12': 'en', '13': 'dans les', '14': 'dans le', '15': 'dans le', '16': 'en', '17': 'en', '18': 'dans le', '19': 'en',
  '2A': 'en', '2B': 'en', '21': 'en', '22': 'dans les', '23': 'dans la', '24': 'en', '25': 'dans le', '26': 'dans la', '27': "dans l'", '28': 'en', '29': 'dans le',
  '30': 'dans le', '31': 'en', '32': 'dans le', '33': 'en', '34': "dans l'", '35': 'en', '36': "dans l'", '37': 'en', '38': 'en', '39': 'dans le',
  '40': 'dans les', '41': 'dans le', '42': 'dans la', '43': 'en', '44': 'en', '45': 'dans le', '46': 'dans le', '47': 'dans le', '48': 'en', '49': 'en',
  '50': 'dans la', '51': 'dans la', '52': 'en', '53': 'en', '54': 'en', '55': 'dans la', '56': 'dans le', '57': 'en', '58': 'dans la', '59': 'dans le',
  '60': "dans l'", '61': "dans l'", '62': 'dans le', '63': 'dans le', '64': 'dans les', '65': 'dans les', '66': 'dans les', '67': 'dans le', '68': 'dans le', '69': 'dans le',
  '70': 'en', '71': 'en', '72': 'dans la', '73': 'en', '74': 'en', '75': 'à', '76': 'en', '77': 'en', '78': 'dans les', '79': 'dans les',
  '80': 'dans la', '81': 'dans le', '82': 'dans le', '83': 'dans le', '84': 'dans le', '85': 'en', '86': 'dans la', '87': 'en', '88': 'dans les', '89': "dans l'",
  '90': 'dans le', '91': 'en', '92': 'dans les', '93': 'en', '94': 'dans le', '95': 'dans le',
  '971': 'en', '972': 'en', '973': 'en', '974': 'à', '976': 'à',
}

// « dans la Loire », « en Isère », « dans l'Ain » ; « dans le département X » si le code est inconnu
export const inDepartment = (codeDepartement: string, departement: string) => {
  const preposition = DEPARTMENT_PREPOSITIONS[codeDepartement.padStart(2, '0')]
  if (!preposition) return `dans le département ${departement}`
  return preposition.endsWith("'") ? `${preposition}${departement}` : `${preposition} ${departement}`
}

// préposition d'usage devant un nom de région, par code INSEE (« en Bretagne », « dans le Grand Est », « dans les Hauts-de-France »…)
const REGION_PREPOSITIONS: Record<string, string> = {
  '01': 'en', '02': 'en', '03': 'en', '04': 'à', '06': 'à',
  '11': 'en', '24': 'en', '27': 'en', '28': 'en', '32': 'dans les', '44': 'dans le', '52': 'dans les',
  '53': 'en', '75': 'en', '76': 'en', '84': 'en', '93': 'en', '94': 'en',
}

// « en Auvergne-Rhône-Alpes », « dans les Pays de la Loire » ; « dans la région X » si le code est inconnu
export const inRegion = (codeRegion: string, region: string) => {
  const preposition = REGION_PREPOSITIONS[codeRegion.padStart(2, '0')]
  return preposition ? `${preposition} ${region}` : `dans la région ${region}`
}

export const formatDate = (date: string) => {
  const options = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  } as Intl.DateTimeFormatOptions

  const formatted = new Date(date).toLocaleDateString('fr-FR', options)
  return formatted
}

export const CLOUDINARY_ORIGIN = 'https://res.cloudinary.com'

export const cloudinaryUrl = (
  { src, width, quality, crop, format, params = [] }: {
    src: string,
    width?: number | string,
    /* nombre ou niveau de q_auto (ex : 'auto:eco') */
    quality?: number | string,
    crop?: string,
    /* format forcé (ex : 'avif') ; f_auto par défaut */
    format?: string,
    params?: string[]
  }) => {
  // Local images svg
  if (src.endsWith('.svg')) {
    return src
  }

  // External image to be Optimized
  let method = 'upload'
  const finalParams = [`f_${format || 'auto'}`, `c_${crop || 'fill'}`, width ? `w_${width}` : '', `q_${quality || 'auto'}`, ...params]
  
  if (src.startsWith('http')) {
    method = 'fetch'
  } 

  return `${CLOUDINARY_ORIGIN}/${process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME}/image/${method}/${finalParams.filter(r => r !== '').join(',')}/${encodeURIComponent(src)}`
}

export const genericBuyRoutes = {
  'buy': () => isGroup ? `/achat/annonces` : `/acheter`,
  'hub-estim-immo-city-buy': () => '/hub/estimation-immobiliere-commune-vente',
  'hub-apt-city-buy': () => '/hub/vente-appartement-commune',
  'hub-apt-dep-buy': () => '/hub/vente-appartement-departement',
  'hub-immo-city-buy': () => '/hub/vente-immobiliere-commune',
  'hub-immo-dep-buy': () => '/hub/vente-immobiliere-departement',
  'hub-house-city-buy': () => '/hub/vente-maison-commune',
  'hub-house-dep-buy': () => '/hub/vente-maison-departement',
}

export const genericRentRoutes = {
  'rent': () =>  isGroup ? `/location/annonces` : `/louer`,
  'hub-estim-immo-city-rent': () => '/hub/estimation-immobiliere-commune-location',
  'hub-apt-city-rent': () => '/hub/location-appartement-commune',
  'hub-apt-dep-rent': () => '/hub/location-appartement-departement',
  'hub-immo-city-rent': () => '/hub/location-immobiliere-commune',
  'hub-immo-dep-rent': () => '/hub/location-immobiliere-departement',
  'hub-house-city-rent': () => '/hub/location-maison-commune',
  'hub-house-dep-rent': () => '/hub/location-maison-departement',
}

export const genericRoutes = {
  'agents-root': () => '/agent-immobilier',
  'agents-list': () => '/agent-immobilier/nos-conseillers',
  'blog': () => '/blog',
  'calculators': () => '/calculatrices-financieres',
  'calculator-capacity': () => '/calculatrices-financieres/capacite-emprunt',
  'calculator-notary': () => '/calculatrices-financieres/frais-de-notaire',
  'calculator-loan': () => '/calculatrices-financieres/pret-immobilier',
  'account': () => '/compte',
  'estimate': () => '/estimation-immobiliere',
  'manage': () => '/faire-gerer-son-bien-immobilier',
  'mentions': () => '/mentions-legales',
  'discovery': () => '/transmettre-mes-criteres',
  // Hubs
  'hub-agents-city': () => '/hub/conseillers-immobilier-commune',
  'hub-immo-city': () => '/hub/immobilier-commune',
  'hub-immo-dep': () => '/hub/immobilier-departement',
  // Biens vendu
  'sold': () => '/biens-vendus',
}

export const routeMapping = {
  ...genericRoutes,
  ...genericRentRoutes,
  ...genericBuyRoutes,
  'agent': (fragments: {[key: string]: string}) => `/agent-immobilier/${makeSlug(fragments.slug)}`,
  'agents-city': (fragments: {[key: string]: string}) => `/agent-immobilier/ville/${makeSlug(fragments.city)}`,
  'article': (fragments: {[key: string]: string}) => `/blog/${makeSlug(fragments.slug)}`,
  'estimate-rent': (fragments: {[key: string]: string}) => `/estimation-immobiliere/${makeSlug(fragments.city)}/location`,
  'estimate-buy': (fragments: {[key: string]: string}) => `/estimation-immobiliere/${makeSlug(fragments.city)}/vente`,
  'privacy-policy': () => 'https://www.ecosysteme.ideeri.fr/politique-de-protection-des-donnees-personnelles',
  // 
  'ads-city-buy': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/achat/${makeSlug(fragments.city)}`
      : `/immobilier-local/${makeSlug(fragments.city)}/achat`,
  'ads-city-rent': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/location/${makeSlug(fragments.city)}`
      : `/immobilier-local/${makeSlug(fragments.city)}/location`,
  // Commune
  'ads-city-buy-new': (fragments: {[key: string]: string}) =>
     `/immobilier-neuf/${makeSlug(fragments.city)}`,
  'ads-city-sold': (fragments: {[key: string]: string}) =>
    `/biens-vendus/${makeSlug(fragments.city)}`,
  'ads-city-apt-buy': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/achat/${makeSlug(fragments.city)}/appartements`
      : `/immobilier-local/${makeSlug(fragments.city)}/achat/appartements`,
  'ads-city-apt-rent': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/location/${makeSlug(fragments.city)}/appartements`
      : `/immobilier-local/${makeSlug(fragments.city)}/location/appartements`,
  'ads-city-house-buy': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/achat/${makeSlug(fragments.city)}/maisons`
      : `/immobilier-local/${makeSlug(fragments.city)}/achat/maisons`,
  'ads-city-house-rent': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/location/${makeSlug(fragments.city)}/maisons`
      : `/immobilier-local/${makeSlug(fragments.city)}/location/maisons`,
  // Departement
  'ads-dep-buy-new': (fragments: {[key: string]: string}) =>
    `/immobilier-neuf/departement/${makeSlug(fragments.department)}`,
  'ads-dep-sold': (fragments: {[key: string]: string}) =>
    `/biens-vendus/departement/${makeSlug(fragments.department)}`,
  'ads-dep-buy': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/achat/departement/${makeSlug(fragments.department)}`
      : `/immobilier-local/departement/${makeSlug(fragments.department)}/achat`,
  'ads-dep-rent': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/location/departement/${makeSlug(fragments.department)}`
      : `/immobilier-local/departement/${makeSlug(fragments.department)}/location`,
  'ads-dep-apt-buy': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/achat/departement/${makeSlug(fragments.department)}/appartements`
      : `/immobilier-local/departement/${makeSlug(fragments.department)}/achat/appartements`,
  'ads-dep-apt-rent': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/location/departement/${makeSlug(fragments.department)}/appartements`
      : `/immobilier-local/departement/${makeSlug(fragments.department)}/location/appartements`,
  'ads-dep-house-buy': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/achat/departement/${makeSlug(fragments.department)}/maisons`
      : `/immobilier-local/departement/${makeSlug(fragments.department)}/achat/maisons`,
  'ads-dep-house-rent': (fragments: {[key: string]: string}) =>
    isGroup
      ? `/location/departement/${makeSlug(fragments.department)}/maisons`
      : `/immobilier-local/departement/${makeSlug(fragments.department)}/location/maisons`,
  // Region
  'ads-region-buy-new': (fragments: {[key: string]: string}) =>
    `/immobilier-neuf/region/${makeSlug(fragments.region)}`,
  'ads-region-sold': (fragments: {[key: string]: string}) =>
    `/biens-vendus/region/${makeSlug(fragments.region)}`,
  //
  'immo': (fragments: {[key: string]: string}) =>
    isGroup
    ? `/immobilier${fragments.region ? `/${makeSlug(fragments.region)}`: ''}${fragments.department ? `/${makeSlug(fragments.department)}`: ''}${fragments.city ? `/${makeSlug(fragments.city)}`: ''}`
    // site agence : les pages ville ne sont pas sous le département
    : `/immobilier-local${fragments.city ? `/${makeSlug(fragments.city)}` : fragments.department ? `/departement/${makeSlug(fragments.department)}` : ''}`,
  'immo-city': (fragments: {[key: string]: string}) =>
    isGroup
    ? `/immobilier${fragments.region ? `/${makeSlug(fragments.region)}`: ''}${fragments.department ? `/${makeSlug(fragments.department)}`: ''}${fragments.city ? `/${makeSlug(fragments.city)}`: ''}`
    : `/immobilier-local${fragments.city ? `/${makeSlug(fragments.city)}` : ''}`,
  'immo-new': (fragments: {[key: string]: string}) => `/immobilier-neuf${fragments.city ? `/${makeSlug(fragments.city)}` : ''}${fragments.program ? `/${makeSlug(fragments.program)}`: ''}${fragments.slug ? `/${makeSlug(fragments.slug)}` : ''}`,
  // Agency only
  'immo-rent': (fragments: {[key: string]: string}) => `/immobilier-local${ fragments.department ? `/departement/${makeSlug(fragments.department)}` :''}${fragments.city ? `/${makeSlug(fragments.city)}` : ''}/location`,
  'immo-buy': (fragments: {[key: string]: string}) => `/immobilier-local${ fragments.department ? `/departement/${makeSlug(fragments.department)}` :''}${fragments.city ? `/${makeSlug(fragments.city)}` : ''}/achat`,
  // Portal only
  'ads-buy': () => `/achat/annonces`,
  'ads-rent': () => `/location/annonces`,
  'about': () => '/a-propos',
  'agency-hp': (fragments: {[key: string]: string}) => `/agence-immobiliere/agence-${makeSlug(fragments.city)}`,
  'agency-buy': (fragments: {[key: string]: string}) => `/agence-immobiliere/agence-${makeSlug(fragments.city)}/acheter`,
  'agency-rent': (_fragments: {[key: string]: string}) => isGroup ? `/location/annonces` : `/louer`,
  'agencies': () => `/nos-agences`,
  'ads-region-buy': (fragments: {[key: string]: string}) => `/achat/region/${makeSlug(fragments.region)}`,
  'ads-region-rent': (fragments: {[key: string]: string}) => `/location/region/${makeSlug(fragments.region)}`,
  'ads-region-apt-buy': (fragments: {[key: string]: string}) => `/achat/region/${makeSlug(fragments.region)}/appartements`,
  'ads-region-apt-rent': (fragments: {[key: string]: string}) => `/location/region/${makeSlug(fragments.region)}/appartements`,
  'ads-region-house-buy': (fragments: {[key: string]: string}) => `/achat/region/${makeSlug(fragments.region)}/maisons`,
  'ads-region-house-rent': (fragments: {[key: string]: string}) => `/location/region/${makeSlug(fragments.region)}/maisons`
} as const

// lettres que la décomposition Unicode (NFD) ne ramène pas à l'ASCII
const LIGATURES: Record<string, string> = { œ: 'oe', Œ: 'OE', æ: 'ae', Æ: 'AE', ß: 'ss', ø: 'o', Ø: 'O', đ: 'd', Đ: 'D', ł: 'l', Ł: 'L' }

/**
 * slug d'URL : mêmes résultats que le package `slug` (vérifié sur les ~35 000 communes et les
 * données agences), sans lui : il embarquait le polyfill Buffer (~8 KB gzip) dans le JS client.
 * Accents retirés, tirets et espaces fusionnés en un seul tiret, autres caractères supprimés.
 */
export const makeSlug = (string?: string) => string
  ? string
    .replace(/[œŒæÆßøØđĐłŁ]/g, letter => LIGATURES[letter])
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/-/g, ' ')
    .replace(/[^A-Za-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase()
  : ''

type Route = {
  key: keyof typeof routeMapping,
  fragments?: {[key: string]: string},
  params?: SearchParamsType
}

export const getRoute = ({ key, fragments = {}, params }: Route) => {
  let url = routeMapping[key](fragments)
    .replace(/([^:])\/\/+/g, '$1/')  // collapse // but preserve ://
    .replace(/\/+$/, '')              // strip trailing slash

  if (params) {
    url = url + `?${makeUrlParams(params)}`
  }

  return url
}

export type SearchParamsType = string | string[][] | Record<string, string> | URLSearchParams | undefined
// Make url prarams from Object
export const makeUrlParams = (paramsObj: SearchParamsType) => {
  const searchParams = new URLSearchParams(paramsObj)
  return searchParams.toString()
}

export const extractIndexedParam = (url: string, paramName: string) => {
  const params = new URL(url).searchParams;
  const regex = new RegExp(`^${paramName}\\[\\d+\\]$`);
  const result = [];

  for (const [key, value] of params.entries()) {
    if (regex.test(key)) {
      result.push(value);
    }
  }

  return result;
}

export const sortAplha = (a: {city: string}, b: {city: string}) => {
  const nameA = a.city.toUpperCase(); // ignore upper and lowercase
  const nameB = b.city.toUpperCase(); // ignore upper and lowercase
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  // names must be equal
  return 0;
}